import { useQuery } from '@tanstack/react-query';
import { getVideoPersons } from '@/services/videoApi';
import type { VideoPersonsResponse } from '@/types/api';

/**
 * Fetches detected persons for a given video.
 *
 * When person_count === 1 the caller should skip the PersonPicker and
 * silently treat person 0 as the subject. Only fetch when a videoId is known.
 *
 * Results are cached by React Query using the key ['videoPersons', videoId].
 */
export function useVideoPersons(videoId: string | undefined) {
  return useQuery<VideoPersonsResponse, Error>({
    queryKey: ['videoPersons', videoId],
    queryFn: () => getVideoPersons(videoId!),
    enabled: !!videoId,
    // Person detection for a given video frame is stable — no need to re-fetch
    staleTime: Infinity,
    // Retry with exponential backoff to absorb the pose-extraction window
    // (~2-5s) that runs synchronously after video upload.
    // Delays: attempt 0 → 1s, 1 → 2s, 2 → 4s (capped).
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    // Bypass stale-while-revalidate so retries always hit the network.
    networkMode: 'always',
  });
}
